import React, { useEffect } from 'react';
import { format } from 'date-fns';
import { useHistory } from 'react-router-dom';

import { Box, Button, Checkbox, Heading, Table, Text, TextButton } from '@zillow/constellation-10';
import { IconChevronDownFilled, IconChevronUpFilled } from '@zillow/constellation-icons';
import { useIsVisible } from '@zg-rentals/react-hooks';
import { LoadingConstants, SpinnerContainer } from '@zg-rentals/rental-manager-env';
import {
  AuthCheckWrapper,
  HeadWrapper,
  numberUtils,
  useIsScreenDesktop,
  useRentalManagerUser,
  useRmwPaths,
} from '@zg-rentals/rental-manager-env';
import {
  type AdproductsZuoraBillingInvoiceDto,
  type AdproductsZuoraInvoicePaymentDto,
  ZuoraInvoiceType,
} from '@zg-rentals/ts-adproducts-client';

import BillingConstants from '../../constants/BillingConstants';

import BillingTopBanner from '../../components/BillingTopBanner';
import BillingError from '../../components/BillingError';
import BillingEmptyBanner from '../../components/BillingEmptyBanner';
import BillingTableErrorRow from '../../components/billingTableErrorRow/BillingTableErrorRow';

import billingUtils from '../../utils/billingUtils';

import type { InvoiceListFilterConfig } from '../../hooks/useInvoiceList';
import { useInvoiceList } from '../../hooks/useInvoiceList';
import { useAppMaintenance } from '../../hooks/useMaintenance';
import { InvoicePaymentDisplay } from '../../components/InvoicePaymentDisplay';

export type ColumnConfig<T> = {
  width: number;
  header: string;
  align: 'left' | 'center' | 'right';
  dataKey?: keyof T;
  renderer?: (data: T) => React.ReactNode;
  className?: string;
};

type BillingInvoicePageProps = {
  emptyMessage: {
    text: Array<string>;
    title: string;
  };
  fullColumnConfig: Array<ColumnConfig<AdproductsZuoraBillingInvoiceDto>>;
  excludeNucleus?: boolean;
  headerLeft?: React.ReactNode;
  isPaymentDue: boolean;
  mobileColumnConfig: Array<ColumnConfig<AdproductsZuoraBillingInvoiceDto>>;
  showHistory: boolean;
  title: string;
};

const formatDate = (value: string | number) => value && format(new Date(value), 'MM/dd/yyyy hh:mm aa');
const rowSelectionDisabled = (rowData: AdproductsZuoraBillingInvoiceDto) =>
  rowData.invoiceType === ZuoraInvoiceType.NUCLEUS_LISTING;

const renderCell = <T,>(col: ColumnConfig<T>, row: T) => {
  if (col.renderer) return col.renderer(row);
  if (col.dataKey) return String(row[col.dataKey] ?? '');
  return '';
};

export const BillingInvoicePage = ({
  emptyMessage,
  excludeNucleus,
  fullColumnConfig,
  headerLeft,
  isPaymentDue,
  mobileColumnConfig,
  title,
  showHistory,
}: BillingInvoicePageProps) => {
  const {
    invoices,
    hasMoreData,
    fetchBillingData,
    loadingState,
    errorMsg,
    fetchPropertyData,
    setFilters,
    filters,
    refreshInvoices,
  } = useInvoiceList({
    hasPayments: !isPaymentDue || false,
    excludeNucleus,
  });
  const isScreenDesktop = useIsScreenDesktop();
  const { user } = useRentalManagerUser();
  const paths = useRmwPaths();
  const paymentsEnabled = user.features.canAccessInvoicePayments;
  const { isMaintenanceMode } = useAppMaintenance();
  const isNucleusOnly = billingUtils.isOnlyNucleusUser(user);
  const tempColumnConfig = isScreenDesktop ? fullColumnConfig : mobileColumnConfig;
  const columnConfig = tempColumnConfig.slice(0);

  const filteredInvoices = invoices.slice(0);
  const [selected, setSelected] = React.useState(new Set<number>());
  const [initialLoadComplete, setInitialLoadComplete] = React.useState(loadingState === LoadingConstants.SUCCESS);
  const [expanded, setExpanded] = React.useState<string | null>(null);
  const history = useHistory();
  const { ref: sentinelRef, isVisible: isSentinelVisible } = useIsVisible<HTMLDivElement>({ threshold: 0.1 });

  const data = filteredInvoices;

  const handlePayInvoiceClick = (invoiceId: string) => {
    history.push(`${paths.billingPage}/${paths.invoicePage}/${invoiceId}/payment`);
  };

  const handleEditClick = (accountNumber: string) => {
    history.push(`${paths.billingPage}/account/${accountNumber}`);
  };

  React.useEffect(() => {
    if (loadingState === LoadingConstants.SUCCESS) {
      setInitialLoadComplete(true);
    }
  }, [loadingState]);

  if (isPaymentDue && paymentsEnabled) {
    columnConfig.push({
      width: 1,
      header: 'Auto Pay',
      renderer: (invoice) => {
        if (!invoice.canMakePayments) {
          return '-';
        }
        if (billingUtils.invoiceInAutopayRange(invoice)) {
          return (
            <TextButton onClick={() => handleEditClick(invoice.accountNumber)} disabled={isMaintenanceMode}>
              {invoice.accountAutopay ? 'ON' : 'OFF'}
            </TextButton>
          );
        }
        return '-';
      },
      align: 'center',
    });
    columnConfig.push({
      width: 1,
      header: '',
      renderer: (invoice) =>
        invoice.canMakePayments ? (
          <Button
            tone="brand"
            emphasis="filled"
            onClick={() => handlePayInvoiceClick(invoice.invoiceNumber)}
            disabled={isMaintenanceMode}
          >
            Pay
          </Button>
        ) : undefined,
      align: 'right',
    });
  }

  const handleSaveFilters = (newFilters: InvoiceListFilterConfig) => {
    setFilters(newFilters);
    setSelected(new Set());
    setExpanded(null);
  };

  const showSelection = !isNucleusOnly;

  const handleSelectRow = (i: number) => {
    const newSelected = new Set(selected);
    if (newSelected.has(i)) {
      newSelected.delete(i);
    } else {
      newSelected.add(i);
    }
    setSelected(newSelected);
  };

  const handleSelectAll = () => {
    const selectableCount = data.filter((row) => !rowSelectionDisabled(row)).length;
    if (selected.size >= selectableCount) {
      setSelected(new Set());
    } else {
      const newSelected = new Set<number>();
      data.forEach((row, i) => {
        if (!rowSelectionDisabled(row)) {
          newSelected.add(i);
        }
      });
      setSelected(newSelected);
    }
  };

  const handleToggleExpanded = (invoiceNumber: string) => {
    setExpanded((prev) => (prev === invoiceNumber ? null : invoiceNumber));
  };

  const isRowExpandable = (rowData: AdproductsZuoraBillingInvoiceDto) =>
    showHistory && rowData.invoicePayments && rowData.invoicePayments.length > 0;

  useEffect(() => {
    if (isSentinelVisible && hasMoreData && loadingState !== LoadingConstants.LOADING) {
      fetchBillingData();
    }
  }, [isSentinelVisible, hasMoreData, loadingState, fetchBillingData]);

  React.useEffect(() => {
    if (loadingState === LoadingConstants.INITIAL) {
      fetchBillingData();
      fetchPropertyData();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- refetch when state resets to INITIAL (e.g. after payment)
  }, [loadingState]);

  React.useEffect(() => {
    if (loadingState === LoadingConstants.ERROR) {
      refreshInvoices();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- retry on remount when cached state is ERROR
  }, []);

  const renderEmptyBanner = () => {
    if (filteredInvoices.length === 0 && loadingState === LoadingConstants.SUCCESS) {
      return <BillingEmptyBanner notification={emptyMessage} />;
    }
    return null;
  };

  const historyColumnConfig: Array<ColumnConfig<AdproductsZuoraInvoicePaymentDto>> = [
    {
      width: 1,
      header: '',
      renderer: () => ' ',
      align: 'right',
    },
    {
      width: 2,
      header: 'Date',
      renderer: (value: AdproductsZuoraInvoicePaymentDto) => formatDate(value.createdTime),
      align: 'left',
    },
    {
      width: 3,
      header: 'Amount',
      renderer: (value: AdproductsZuoraInvoicePaymentDto) => numberUtils.asCurrency(value.appliedPaymentAmount),
      align: 'center',
    },
    {
      width: 3,
      header: 'Payment Number',
      dataKey: 'paymentNumber',
      align: 'center',
    },
    {
      width: 3,
      header: 'Status',
      dataKey: 'status',
      align: 'center',
      renderer: (value) => <InvoicePaymentDisplay value={value} />,
    },
  ];

  if (paymentsEnabled) {
    historyColumnConfig.splice(1, 0, {
      width: 3,
      header: 'Payment Method',
      align: 'center',
      renderer: billingUtils.formatPaymentMethod,
    });
    historyColumnConfig.push({
      width: 3,
      header: 'Paid By',
      align: 'center',
      renderer: (value) => (value.email ? value.email : '-'),
    });
  }

  const renderPaymentHistoryTable = (rowData: AdproductsZuoraBillingInvoiceDto) => {
    const payments = rowData.invoicePayments.slice().sort((a, b) => b.createdTime - a.createdTime);
    const visibleColumns = historyColumnConfig.filter((col) => col.header !== '');
    return (
      <tr>
        <td colSpan={columnConfig.length + (showSelection ? 1 : 0) + 1}>
          <Box
            css={{
              backgroundColor: 'bg.action.soft.selected.default',
              borderLeftWidth: '1px',
              borderLeftStyle: 'solid',
              borderLeftColor: 'border.accent.brand.hero',
              padding: 'sm',
            }}
          >
            <Heading level={6} css={{ paddingBottom: 'sm' }}>
              Payment History
            </Heading>
            {isScreenDesktop ? (
              <Table.Root aria-label="Payment history" appearance="horizontal" css={{ width: '100%' }}>
                <Table.Header>
                  <Table.Row>
                    {historyColumnConfig.map((col, ci) => (
                      <Table.HeaderCell key={ci} css={{ textAlign: col.align }}>
                        {col.header}
                      </Table.HeaderCell>
                    ))}
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {payments.map((payment) => (
                    <Table.Row key={payment.id}>
                      {historyColumnConfig.map((col, ci) => (
                        <Table.Cell key={ci} css={{ textAlign: col.align }}>
                          {renderCell(col, payment)}
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            ) : (
              <Box css={{ display: 'flex', flexDirection: 'column', gap: 'sm' }}>
                {payments.map((payment) => (
                  <Box
                    key={payment.id}
                    css={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 'xs',
                      backgroundColor: 'bg.neutral',
                      borderBottom: '1px solid',
                      borderBottomColor: 'border.muted',
                      borderRadius: 'sm',
                      padding: 'sm',
                    }}
                  >
                    {visibleColumns.map((col, ci) => (
                      <React.Fragment key={ci}>
                        <Text textStyle="body-sm-bold">{col.header}</Text>
                        <Text textStyle="body-sm">{renderCell(col, payment)}</Text>
                      </React.Fragment>
                    ))}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </td>
      </tr>
    );
  };

  const renderContent = () => {
    const isLoading = loadingState === LoadingConstants.LOADING || loadingState === LoadingConstants.INITIAL;
    const showError = loadingState === LoadingConstants.ERROR;
    const showInfiniteScroll = hasMoreData && !showError;

    return (
      <>
        <Table.Root
          aria-label={title}
          appearance="horizontal"
          css={{ width: '100%', tableLayout: 'fixed', borderTop: '1px solid', borderTopColor: 'border.muted' }}
        >
          <Table.Header css={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: 'bg.neutral' }}>
            <Table.Row>
              {showSelection && (
                <Table.HeaderCell css={{ width: '40px' }}>
                  <Checkbox
                    name="select-all"
                    aria-label="Select all invoices"
                    checked={selected.size > 0 && selected.size === data.filter((r) => !rowSelectionDisabled(r)).length}
                    onChange={handleSelectAll}
                  />
                </Table.HeaderCell>
              )}
              {columnConfig.map((col, i) => (
                <Table.HeaderCell key={i} css={{ textAlign: col.align }}>
                  {col.header}
                </Table.HeaderCell>
              ))}
              {showHistory && <Table.HeaderCell css={{ width: '40px', textAlign: 'center' }}>{''}</Table.HeaderCell>}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {data.map((row, i) => {
              const canExpand = isRowExpandable(row);
              const isExpanded = expanded === row.invoiceNumber;
              const isSelected = selected.has(i);
              const isDisabled = rowSelectionDisabled(row);
              return (
                <React.Fragment key={row.invoiceNumber}>
                  <Table.Row
                    aria-selected={isSelected}
                    css={isSelected ? { backgroundColor: 'bg.action.soft.selected.default' } : undefined}
                  >
                    {showSelection && (
                      <Table.Cell css={{ width: '40px' }}>
                        <Checkbox
                          name={`select-row-${i}`}
                          aria-label={`Select invoice ${row.invoiceNumber}`}
                          checked={isSelected}
                          onChange={() => handleSelectRow(i)}
                          disabled={isDisabled}
                        />
                      </Table.Cell>
                    )}
                    {columnConfig.map((col, ci) => (
                      <Table.Cell key={ci} css={{ textAlign: col.align }}>
                        {renderCell(col, row)}
                      </Table.Cell>
                    ))}
                    {showHistory && (
                      <Table.Cell css={{ width: '40px', textAlign: 'center' }}>
                        {canExpand && (
                          <TextButton
                            aria-label={isExpanded ? 'Hide payment history' : 'Show payment history'}
                            onClick={() => handleToggleExpanded(row.invoiceNumber)}
                            css={{ display: 'inline-flex' }}
                          >
                            {isExpanded ? (
                              <IconChevronUpFilled width={16} height={16} />
                            ) : (
                              <IconChevronDownFilled width={16} height={16} />
                            )}
                          </TextButton>
                        )}
                      </Table.Cell>
                    )}
                  </Table.Row>
                  {canExpand && isExpanded && renderPaymentHistoryTable(row)}
                </React.Fragment>
              );
            })}
          </Table.Body>
        </Table.Root>
        {isLoading && <SpinnerContainer />}
        {showError && <BillingTableErrorRow />}
        {showInfiniteScroll && <div ref={sentinelRef} />}
        {renderEmptyBanner()}
      </>
    );
  };

  return (
    <AuthCheckWrapper>
      <div>
        <HeadWrapper title={title} />
        {errorMsg && <BillingError errorMsg={errorMsg} />}
        {loadingState === LoadingConstants.ERROR && <BillingError errorMsg={BillingConstants.INVOICE_LOAD_ERROR} />}
        {!isNucleusOnly && (
          <BillingTopBanner
            selected={selected}
            data={data}
            deselectAll={() => setSelected(new Set())}
            refreshInvoices={refreshInvoices}
            isPaymentDue={isPaymentDue}
            headerLeft={headerLeft}
            filters={filters}
            setFilters={handleSaveFilters}
          />
        )}
        {initialLoadComplete ? renderContent() : <SpinnerContainer />}
        {!isNucleusOnly && (
          <Box
            css={{
              padding: '16px !important',
              marginTop: '40px !important',
              display: 'flex',
              flexWrap: 'wrap',
            }}
          >
            <Box css={{ width: '100%' }}>
              {!paymentsEnabled && (
                <Text
                  textStyle="body-xs-bold"
                  css={{
                    color:
                      'gray.500' /* oxlint-disable-line constellation/prefer-semantic-tokens -- pre-existing base-token usage; was warn-only under ESLint's pandaLint */,
                  }}
                >
                  {BillingConstants.BILLING_INFO_FOOTNOTE}
                </Text>
              )}
            </Box>
          </Box>
        )}
      </div>
    </AuthCheckWrapper>
  );
};

export default BillingInvoicePage;
